import $ from 'jquery';
import * as jose from 'jose';
import CryptoJS from 'crypto-js';
import mixpanel from 'mixpanel-browser';
import { Translate } from 'react-localize-redux';
import { PERMISSION } from '../components/investigation/share/user_roles';
import { difference, isEmpty, isEqual, isObject, xorWith } from 'lodash';
import { ServiceType } from '../pages/hospital/Request/types';
import { resolveCoverageDisplayName } from './coverageLabel';
import { addBreadcrumb, flush as flushBreadcrumbs, getCrashContext } from './diagnostics/breadcrumbs';
import { meetsPasswordPolicy } from './password_rules';
import { daysToAmountAndUnit } from '../components/general/SmartFields/prescriptionDuration';
import {
    dateToFullDateString,
    birthdateToDate,
    formatBirthdateForDisplay,
    areSameDates,
    yearsFromDate,
} from './birthdate';
export { dateToFullDateString, birthdateToDate, formatBirthdateForDisplay, areSameDates, yearsFromDate };
/**
 * Function that validates fields from anywhere in the app
 * 
 * @param {Object} field - Field to be validated
 * @param {string} field.value - The value of the input
 * @param {string} field.validation - The type of validations to be done
 * @param {boolean} field.required - The type of validations to be done
 * 
 * @returns {Object}  - This object contains the result and the message
 * @returns {boolean} result - This object contains the result and the message
 * @returns {string} messageCode - Code message to be encoded
 */
export function validateField(field, fieldCompare){
    const response = {};
    // Disposition field: when DECEASED, dateOfDeath and cause are required
    if(field.type === "disposition" && typeof field.value === 'object' && field.value?.disposition === "DECEASED"){
        const pathErroTranslation = "investigation.errors.";
        if(!field.value.dateOfDeath || !(field.value.immediateCause || field.value.cause)){
            response.result = false;
            response.messageCode = pathErroTranslation+"error_deceased_fields_required";
            return response;
        }
        response.result = true;
        response.messageCode = "";
        return response;
    }
    //Si el campo es obligatorio o si tiene un valor, validamos su contenido
    if(field.required || (field.value !== "" && typeof field.value === 'string') ){
        const pathErroTranslation = "investigation.errors.";
        let re;
        //Si no hay validación, la inferimos
        let validationSchema = field.validation;
        if(!validationSchema){
            validationSchema = field.type === "birthdate" ? "pastDate" : "notEmpty"
        }
        switch(validationSchema){
            case "validEmail" : 
                re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                response.result = re.test(String(field.value).toLowerCase());
                response.messageCode = pathErroTranslation+"error_email";
                break;
            case "validPhone" : 
                re = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/;
                response.result = re.test(String(field.value).toLowerCase());
                response.messageCode = pathErroTranslation+"error_phone";
                break;
            case "password":
                response.result = Boolean(field.value && field.value.length > 6);
                response.messageCode =  pathErroTranslation+"error_password"
                break;
            case "textMin2" : 
                response.result = Boolean(field.value && field.value.length >= 2);
                response.messageCode =  pathErroTranslation+"error_length2"
                break;
            case "textMin6" : 
                response.result = Boolean(field.value && field.value.length >= 6);
                response.messageCode =  pathErroTranslation+"error_length6"
                break;
            case "strongPassword" : {
                response.result = meetsPasswordPolicy(field.value);
                response.messageCode = pathErroTranslation+"error_strong_password"
                break;
            }
            case "notEmpty" : 
                response.result = notEmpty(field.value);
                response.messageCode =  pathErroTranslation+"error_not_empty"
                break;
            case "equalTo":
                response.result = Boolean(field.value === fieldCompare);
                response.messageCode =  pathErroTranslation+"error_not_same"
                break;
            case "pastDate":
                // Route the value through birthdateToDate() — the same helper
                // the decrypt and date-picker-display paths already use — so
                // every DOB wire format validates consistently: ms-epoch
                // number, ms-epoch digit string (what a legacy DOB decrypts
                // to, pre-SHER-196 backfill), and YYYY-MM-DD.
                //
                // Plain `new Date(field.value)` returns Invalid Date for an
                // ms-epoch *string* — only the number form parses. The edit
                // form feeds the raw decrypted value into validation whenever
                // the picker is left untouched, so an un-backfilled DOB was
                // silently rejected as "not a past date" (SHER-234).
                {
                    const parsed = birthdateToDate(field.value);
                    response.result = parsed !== null && (parsed.getTime() - Date.now() < 0);
                }
                response.messageCode =  pathErroTranslation+"error_past_date"
                break;
            case "arrayOrFalse":
                response.result = Array.isArray(field.value) || (field.value === false)
                response.messageCode =  pathErroTranslation+"error_notarrayorfalse"
                break;
            case "number":
                response.result = !isNaN(field.value);
                response.messageCode =  pathErroTranslation+"error_notnumber"
                break;
            case "time":
                re = /^\d{2}:\d{2}$/;
                response.result = re.test(String(field.value));
                response.messageCode = pathErroTranslation+"error_time";
                break;
            default:
                console.log("Validación no definida");
                response.result = false;
                response.messageCode =  pathErroTranslation+"error_not_defined"
                break;
        }
    }
    else{
        response.result = true;
        response.messageCode =  "";
    }
    return response;   
}


export function formatDateByLocale(locale){
    switch(locale){
        case "fr":
        case "es":
            return "DD/MM/YYYY"
        case "en":
        default:
            return "YYYY/MM/DD";
    }
}

//Wrapper para que funcione en la validación por campo
export function fieldLevelNotEmpty(value){
    return notEmpty(value) ? undefined : "investigation.errors.error_not_empty"
}
export function notEmpty(value){
    return Boolean(value && value !== "");
}
//Wrapper para que funcione en la validación por campo
export function fieldLevelmarkedCheckbox(value){
    return markedCheckbox(value) ? undefined : "investigation.errors.error_not_empty"
}
export function markedCheckbox(value){
    return value === true;
}

export async function generateKey(){
    const genObj = await crypto.subtle.generateKey(
        {
          name: "AES-GCM",
          length: 256
        },
        true,
        ["encrypt", "decrypt"]
      );
    //A partir del objeto cryptoObj obtenemos la clave
    const cryptoObj = await crypto.subtle.exportKey("jwk", genObj);
    const keyString = cryptoObj.k;

    return keyString;
}

export async function importKey(key){
    let keyObj = await crypto.subtle.importKey(
        "jwk",
        {
            alg: "A256GCM",
            ext: true,
            k: key,
            key_ops: ["encrypt", "decrypt"],
            kty: "oct"
        },
        {
            name: "AES-GCM",
            length: 256
        },
        true,
        ["encrypt", "decrypt"]
    );
hello
    return keyObj;
}

export function encryptData(data, key){
    var KeyObj = CryptoJS.AES.encrypt(data, key);
    var ciphertext = KeyObj.toString();
    return ciphertext;
}

export function decryptData(ciphertext, key){
    try{
        var bytes  = CryptoJS.AES.decrypt(ciphertext, key);
        var originalText = bytes.toString(CryptoJS.enc.Utf8);

        return originalText;
    }
    catch(e){
        return "";
    }

}

export const isUserLoggedIn = () => localStorage.getItem("jwt");


export const PERSONAL_FIELDS_FORM = {

    "required":{
        name:"required",
        required : false,
        type:"checkbox",
        label:"investigation.create.edc.required",
        shortLabel: "investigation.table.required",
        validation : "notEmpty"
    },
    "name" : {
        name:"name",
        required : true,
        type:"text",
        label:"investigation.create.edc.name_field",
        shortLabel: "investigation.table.name",
        validation : "textMin2"
    },
    "type" : {
        name:"type",
        required : true,
        type:"select",
        validation : "notEmpty",
        label : "investigation.create.edc.choose",
        shortLabel: "investigation.table.type",
        defaultOption:{"text" : "investigation.create.edc.choose", "value" : ""},
        options:[{"label" : "investigation.create.edc.type_text", "value" : "text"},
                {"label": "investigation.create.edc.type_number", "value" : "number"},
                {"label": "investigation.create.edc.checkbox", "value" : "checkbox"}, 
                {"label": "investigation.create.edc.type_date", "value" : "date"},
                {"label": "investigation.create.edc.textarea", "value" : "textarea"},
                {"label": "investigation.create.edc.dropdown", "value" : "select"},
                {"label": "investigation.create.edc.multioption", "value" : "multioption"},
                {"label": "investigation.create.edc.radio", "value" : "radio"},
                {"label": "investigation.create.edc.evaluation", "value" : "evaluation"},
                {"label": "investigation.create.edc.type_imc", "value" : "imc"}
        ],
        activationValues : ["select", "multioption", "radio", "evaluation"],
        activatedFields:{
            "select" : {
                required : true,
                type:"options",
                validation : "notEmpty",
                label : "investigation.create.edc.choose",
                shortLabel: "investigation.table.type"
            },
            "multioption" : {
                required : true,
                type:"options",
                validation : "notEmpty",
                label : "investigation.create.edc.choose",
                shortLabel: "investigation.table.type"
            },
            "radio" : {
                required : true,
                type:"options",
                validation : "notEmpty",
                label : "investigation.create.edc.choose",
                shortLabel: "investigation.table.type"
            },
            "evaluation" : {
                required : true,
                type:"options",
                validation : "notEmpty",
                label : "investigation.create.edc.choose",
                shortLabel: "investigation.table.type"
            }
        }                                        
    },
    "label" : {
        name:"label",
        required : false,
        type:"text",
        label : "investigation.create.edc.question_field",
        shortLabel: "investigation.table.question",
        validation : "textMin6", 
        size : "s6"
    }
}

export const PERSONAL_DATA_FIELDS = {
    "name" : {
        required : true,
        name: "name",
        encrypted : true,
        type:"text",
        label:"investigation.create.personal_data.fields.name",
        shortLabel:"investigation.create.personal_data.fields.name",
        validation : "textMin2"
    },
    "surnames" : {
        required : true,
        name: "surnames",
        type:"text",
        encrypted : true,
        label:"investigation.create.personal_data.fields.surname",
        shortLabel:"investigation.create.personal_data.fields.surname",
        validation : "textMin2"
    },
    "birthdate" : {
        name: "birthdate",
        required : true,
        type:"date",
        encrypted : true,
        label:"investigation.create.personal_data.fields.birthdate",
        shortLabel:"investigation.create.personal_data.fields.birthdate",
        validation : "pastDate"
    },
    "address" : {
        name: "address",
        required : true,
        type:"text",
        encrypted : true,
        label:"investigation.create.personal_data.fields.address",
        shortLabel:"investigation.create.personal_data.fields.address",
        validation : "textMin2"
    },
    "health_id" : {
        name: "health_id",
        required : true,
        type:"text",
        encrypted : true,
        label:"investigation.create.personal_data.fields.health_id",
        shortLabel:"investigation.create.personal_data.fields.health_id",
        validation : "textMin2"
    },
    "automated_health_id" : {
        name: "automated_health_id",
        required : true,
        type:"text",
        encrypted : false,
        label:"investigation.create.personal_data.fields.automated_health_id",
        shortLabel:"investigation.create.personal_data.fields.automated_health_id",
        validation : "textMin2"
    },
    "national_id" : {
        name: "national_id",
        required : true,
        type:"text",
        encrypted : true,
        label:"investigation.create.personal_data.fields.national_id",
        shortLabel:"investigation.create.personal_data.fields.national_id",
        validation : "textMin2"
    },
    "email" : {
        name: "email",
        required : true,
        type:"text",
        encrypted : true,
        label:"investigation.create.personal_data.fields.email",
        shortLabel:"investigation.create.personal_data.fields.email",
        validation : "validEmail"
    },
    "phone" : {
        name: "phone",
        required : true,
        type:"text",
        encrypted : true,
        label:"investigation.create.personal_data.fields.phone",
        shortLabel:"investigation.create.personal_data.fields.phone",
        validation : "validPhone"
    },
    "sex" : {
        name: "sex",
        required : true,
        type:"select",
        encrypted : true,
        label:"investigation.create.personal_data.fields.sex",
        shortLabel:"investigation.create.personal_data.fields.sex",
        validation : "notEmpty",
        options:[
            {value : "Male", label: "general.male"},
            {value : "Female", label:"general.female"}
        ]
    }
}

export const PERSONAL_DATA_TYPES = {

}

export const FIELDS_BASIC_INFO = {
    "name":{
        name : "name",
        required : true,
        type:"text",
        label:"investigation.create.edc.name",
        validation : "textMin2",
        value: ""
    },
    "acronym":{
        name : "acronym",
        required : true,
        type:"text",
        label:"investigation.create.edc.acronym",
        validation : "notEmpty",
        value: ""
    },
    "type":{
        name : "type",
        required : true,
        type:"select",
        label:"investigation.create.edc.type",
        validation : "notEmpty",
        defaultOption:{"text" : "investigation.create.edc.choose", "value" : ""},
        options : [
                {"label" : "investigation.create.edc.type_study.audit", "value" : "audit"},
                {"label" : "investigation.create.edc.type_study.clinical_research_study", "value" : "clin_res"},
                {"label" : "investigation.create.edc.type_study.medical_device", "value" : "med_dev"},
                {"label" : "investigation.create.edc.type_study.clinical_trial", "value" : "clin_trial"}
            ],
        value: ""
    },
    "description":{
        name : "description",
        required : true,
        type:"textarea",
        label:"investigation.create.edc.description",
        validation : "textMin2",
        value: ""
    },
    "principal_researcher":{
        name : "principal_researcher",
        required : true,
        type:"text",
        label:"investigation.create.edc.principal_researcher",
        validation : "textMin2",
        value: ""
    },
    "other_researcher":{
        name : "other_researcher",
        required : true,
        type:"options",
        element:"text",//El tipo de cada una de las opciones
        label:"investigation.create.edc.other_researchers",
        validation : "textMin2",
        value: ""
    },
    "institution":{
        name : "institution",
        required : true,
        type:"text",
        label:"investigation.create.edc.institution",
        validation : "textMin2",
        value: ""
    },
    "contact":{
        name : "contact",
        required : true,
        type:"text",
        label:"investigation.create.edc.contact",
        validation : "textMin2",
        value: ""
    },
    "ethics_body":{
        name : "ethics_body",
        required : true,
        type:"text",
        label:"investigation.create.edc.ethics_body",
        validation : "textMin2",
        value: ""
    },
    "reference_number_state":{
        name : "reference_number_state",
        required : true,
        type:"select",
        label:"investigation.create.edc.reference_number_state",
        validation : "notEmpty",
        defaultOption:{"text" : "investigation.create.edc.choose", "value" : ""},
        options : [
                {"label" : "investigation.create.edc.reference_number_state_type.not_applicable", "value" : 0},
                {"label" : "investigation.create.edc.reference_number_state_type.pending", "value" : 1},
                {"label" : "investigation.create.edc.reference_number_state_type.approved", "value" : 2},
            ],
        value: ""
    },
    
}

export const templateField = {
    required : true,
    type:"text",
    label:"investigation.create.consent.reason",
    shortLabel: "investigation.create.consent.reason",
    validation : "notEmpty",
    is_personal_data:true
}

export function numberRecordsSection(section, submissions){
    let nRegistros = 0
    for(let i = 0; i < submissions.length; i++){
        const submission = submissions[i];
        
            if(submission.uuid_section === section.uuid){
                nRegistros++;
            }
        
    }
    return nRegistros;
}

export function filterRecordsFromSubmissions(submissions, sectionUUID){
    let filteredSubmissions = [];
    for(let i = 0; i < submissions.length; i++){
        let filteredRecords = [];
        const submission = submissions[i];
        if(submission.uuid_section === sectionUUID){ 
            filteredRecords = submission.surveyRecords; 
        }
      
        if(filteredRecords.length > 0){
            filteredSubmissions.push({
                id:submission.id,
                createdAt:submission.createdAt,
                updatedAt:submission.updatedAt,
                surveyRecords:filteredRecords
            }); 
        }
    }
    return filteredSubmissions;
}

export function filterRecordsFromSection(submission, sectionUUID){
    let filteredRecords = [];
    for(let j = 0; j < submission.surveyRecords.length; j++){
        const record = submission.surveyRecords[j];
        if(record.surveySection.uuid === sectionUUID){ 
            filteredRecords.push(record); 
        }
    }  
    return filteredRecords;
}

export function daysFromDate(fromDateString){
    let fromDate = new Date(fromDateString); 
    let temp = Math.abs(new Date() - fromDate);
    return Math.floor((temp)/(1000 * 60 * 60 * 24));
}

export function saveData(key, value){
    localStorage.setItem(key, value); 
}


export function getData(key){
    return localStorage.getItem(key); 
}

export function decryptPatientsData(patientsData, investigation){
    let tempDecryptedData = [];
    if(patientsData.length !== 0){
        //const rawKeyResearcher = await decryptData("U2FsdGVkX1+vRAPd6EOpOTY53I8LLfs9iyX0mGh1Xesn6rwUS4UnTQvqTyWQvu0VeYLHUScUUtM22K8+4zJqZQ==", "Cabezadesherwood2")
        
        for(const patient of patientsData){
            patient.personalData = decryptSinglePatientData(patient.personalData, investigation);
            tempDecryptedData.push(patient);
        }
    }
    return tempDecryptedData;
}

export function getInvestigationRawKey(uuid, encryptedKeyUsed, keyResearcherInvestigation){
    const nameKey = "rawKeyInvestigation_"+uuid;
    let keyInvestigation = localStorage.getItem(nameKey);
    // SHER-632: an empty cached value is a POISONED entry, not a hit. decryptData
    // returns "" on any failure, so guarding on `!== null` used to make a single
    // failed decrypt (no/wrong rawKeyResearcher, broken key wrap) stick for the
    // whole session and silently empty every later decrypt. Treat "" as a miss so
    // pre-existing poisoned entries self-heal instead of surviving to signOut.
    if(keyInvestigation){
        return keyInvestigation;
    }
    const rawKeyResearcher = encryptedKeyUsed === 0 ? import.meta.env.VITE_APP_DEFAULT_RESEARCH_PASSWORD : localStorage.getItem("rawKeyResearcher");
    keyInvestigation = decryptData(keyResearcherInvestigation, rawKeyResearcher);
    // Only cache a real key — never the "" that decryptData returns on failure.
    if(keyInvestigation){
        localStorage.setItem(nameKey, keyInvestigation);
    }
    return keyInvestigation;
}

export function decryptSinglePatientData(patientPersonalData, investigation){

    let encryptedFields = {};
    if(investigation.permissions !== 0){

        const keyInvestigation = getInvestigationRawKey(investigation.uuid, investigation.encryptedKeyUsed, investigation.keyResearcherInvestigation);

        for(const personalField of investigation.personalFields){
            const encryptedField = patientPersonalData.find(pData =>{
                return pData.name === personalField.name;
            });
            //console.log(personalField.name);

            let decryptedValue = ""
            if(encryptedField){
                const isEncrypted = encryptedField.encrypted === true || personalField.encrypted === true;
                if(isEncrypted){
                    decryptedValue = decryptData(encryptedField.value, keyInvestigation);

                    const fieldType = encryptedField.type || personalField.type;
                    if(fieldType === "date"){
                        if(decryptedValue === "" || decryptedValue === null){
                            console.log("Fecha vacía");
                        }
                        // SHER-193 + UTC-shift bug: keep decrypted value as a STRING.
                        //
                        // Previously this assigned `birthdateToDate(decryptedValue)` (a Date),
                        // but downstream consumers (formatBirthdateForDisplay, yearsFromDate via
                        // birthdateToDate) re-called birthdateToDate on the resulting Date —
                        // which uses UTC getters and shifts the day BACKWARDS by one in any
                        // UTC+ timezone (Spain CEST, UK BST).
                        //
                        // Repro: stored '1999-04-22' → first call returns local-midnight Apr 22
                        // (= UTC Apr 21 23:00 in CEST) → second call's UTC getters read date=21
                        // → returns local-midnight Apr 21 → search results render '21/04/1999'
                        // while the edit form (single-pass) renders '22/04/1999'.
                        //
                        // Keeping the value as a string lets every consumer call birthdateToDate
                        // exactly once. All consumers already accept strings (proven by the
                        // 21 jest cases in src/utils/birthdate.test.ts).
                    }
                }
                else{
                    decryptedValue = encryptedField.value;
                }
            }
            
            encryptedFields[personalField.name] = decryptedValue; 
        }
    }
    
    return encryptedFields;
}

export function dateOrStringToDateString(date, locale){
    if(date instanceof Date){
        return dateToFullDateString(date, locale)
    }
    else{
        return fullDateFromPostgresString(locale, date);
    }
}

export const areArraysEqual = (x, y) => isEmpty(xorWith(x, y, isEqual));

export function hasRequestGroupState(request, status){
    return request.requestsServiceInvestigation.findIndex((req) => req.status === status) !== -1
}

export function hasDiscountsActive(params, permissions){
    if(params.discounts && !params.permissionDiscount){
        return true;
    }
    else if(params.discounts && params.permissionDiscount && permissions.includes(PERMISSION.MAKE_DISCOUNTS)){
        return true;
    }
    else{
        return false;
    }
}

export function dateTimeFromDate(date, localeCode){
    return date.toLocaleString(localeCode,{
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        })
}

/**
 * Parse a timestamp that may be either epoch milliseconds (number or numeric
 * string) or an ISO-8601 string. Treatment stop timestamps are written
 * inconsistently: the offline-first optimistic update stores Date.now().toString()
 * (epoch-ms string) while the backend persists new Date().toISOString() (ISO string).
 * `new Date(Number(isoString))` is `new Date(NaN)` = "Invalid Date", so both shapes
 * must be parsed explicitly. Returns an Invalid Date for empty/"NaN"/nullish input.
 * (SHER-427; same parse hazard as the SHER-193 DOB fix.)
 */
export function epochOrIsoToDate(value){
    if(value === null || value === undefined || value === "" || value === "NaN"){
        return new Date(NaN);
    }
    const str = String(value).trim();
    // All-digit (optionally signed) string -> epoch milliseconds.
    if(/^-?\d+$/.test(str)){
        return new Date(Number(str));
    }
    // Otherwise treat as an ISO-8601 / parseable date string.
    return new Date(str);
}

export function dateAndTimeFromPostgresString(localeCode, dateString){
    const dateObject =  stringDatePostgresToDate(dateString);
    return dateTimeFromDate(dateObject, localeCode);
}

export function timeFromPostgresString(localeCode, dateString, timeZone){
    const dateObject =  stringDatePostgresToDate(dateString);
    return dateObject.toLocaleString(localeCode,{
            hour: '2-digit',
            minute: '2-digit',
            ...(timeZone ? { timeZone } : {}),
            })
}

export function fullDateFromPostgresString(localeCode, dateString, timeZone){
    const dateObject =  stringDatePostgresToDate(dateString);
    return dateObject.toLocaleString(localeCode,{
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            ...(timeZone ? { timeZone } : {}),
            })
}

export function localStringDateFromTimeStamp(localeCode, timeStamp){
    const dateObject = new Date(Number(timeStamp));
    return dateObject.toLocaleString(localeCode,{
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    })
}

/**
 * Safely parses a UTC timestamp string, ensuring timezone-aware parsing
 * Handles both ISO format (with/without timezone) and PostgreSQL format
 * 
 * @param date - Date string from server (may be ISO or PostgreSQL format) or Date object
 * @returns Date object parsed as UTC
 * 
 * @example
 * stringDatePostgresToDate("2024-01-15T10:00:00Z") // Returns Date in UTC
 * stringDatePostgresToDate("2024-01-15 10:00:00") // Assumes UTC, returns Date in UTC
 * stringDatePostgresToDate("2024-01-15T10:00:00+05:00") // Parses with timezone offset
 * stringDatePostgresToDate(new Date()) // Returns Date as-is
 */
export function stringDatePostgresToDate(date){
    // If already a Date object, return as-is
    if (date instanceof Date) {
        return date;
    }

    const str = String(date).trim();
    
    // If string already has timezone info (Z or +/- offset), parse as-is
    if (str.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(str)) {
        return new Date(str);
    }
    
    // If it's PostgreSQL format (space instead of T), convert to ISO
    if (str.includes(' ') && !str.includes('T')) {
        // Replace first space with T, ensure it ends with Z for UTC
        const isoString = str.replace(' ', 'T').replace(' ', '') + 'Z';
        return new Date(isoString);
    }
    
    // If it's ISO format without timezone, append Z to force UTC interpretation
    if (str.includes('T')) {
        return new Date(str + 'Z');
    }
    
    // Fallback: try parsing as-is (may interpret as local time, but better than error)
    return new Date(str);
}

export function researcherFullName(researcher){
    return researcher.name + " " + researcher.surnames;
}

export function patientFullName(patient){
    return `${patient.name} ${patient.surnames}`
}

export function getPatientID(patient){
    return patient.personalData.automated_health_id ? patient.personalData.automated_health_id : patient.personalData.health_id ? patient.personalData.health_id : patient.id;
}

export function getHealthIDLabel(patient){
    if(!patient){
        return "investigation.create.personal_data.fields.id";
    }
    else if(patient.personalData.automated_health_id){
        return "investigation.create.personal_data.fields.automated_health_id";
    }
    else if(patient.personalData.health_id){
        return "investigation.create.personal_data.fields.health_id";
    }
    else{
        return "investigation.create.personal_data.fields.id";
    }
}

export function formatPatients(patients, personalFields, code = 'es-ES', insurances = [], coverages = []){
    const arrayPatients = patients.map(patient => {
        let tempRow = {};
        for(const pField of personalFields){
            if(patient.personalData){
                let value = patient.personalData[pField.name];
                if(pField.type === "date"){
                    value = formatBirthdateForDisplay(patient.personalData[pField.name], code).replace(/\./g, '/');
                }
                if(pField.name === 'sex'){
                    value = <Translate id={`hospital.analytics.graphs.sex.${patient.personalData[pField.name].toLowerCase()}`} />
                }
                if(pField.name === 'coverage'){
                    // Lookup coverage by ID and display coverage name
                    const coverage = coverages.find(cov => cov.id === parseInt(patient.personalData[pField.name]));
                    if(coverage){
                        value = resolveCoverageDisplayName(coverage, insurances);
                    }
                    else{
                        value = "";
                    }
                }
                tempRow[pField.name] = value;
            }
            
        }
        const dateObject =  patient.dateCreated;
        tempRow["dateCreated"] = dateObject.toLocaleString(code,{
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
            })
        tempRow["id"] = patient.uuid;
        tempRow["status"] = patient.status !== undefined ? patient.status : 0;
        tempRow["dateOfDeath"] = patient.dateOfDeath || null;
        return(
            tempRow
        )
    });

    return arrayPatients;
}

export function isSmartField(type){
    // SHER-172: accept all three diagnosis-field aliases (post/partial/pre-migration)
    return ["icd", "icd11", "ict", "allergy", "background", "family-background", "treatment", "treatment_prescription", "treatment_regular", "treatment_inpatient", "bmi", "edd", "request_lab", "request_img"].includes(type);
}

export function getIndexedDB(){
    const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;

    return indexedDB;
}

export function isImageType(typeImage){
    return (typeImage === "image/png" || typeImage === "image/jpeg" || typeImage === "image/jpg");
}
export function openStore(storeName){
    return new Promise(
        function(resolve, reject) {
            const db = getIndexedDB();
            if (!db) {
                return null;
            }
            let request = db.open(storeName, 3);

            request.onerror = function(event) {
                // Hacer algo con request.errorCode!
                reject(Error("Error text"));
            };
            request.onsuccess = function(event) {
                // Hacer algo con request.result!
            };
      
            request.onupgradeneeded = function(event) {
              // Objectstore does not exist. Nothing to load
              event.target.transaction.abort();
              reject(Error('Not found'));
            };
      
            request.onsuccess = function(event) {
                resolve(event.target.result);
            //   var database      = event.target.result;
            //   var transaction   = database.transaction([storeName]);
            //   var objectStore   = transaction.objectStore(storeName);
            //   var objectRequest = objectStore.get(id);
      
            //   objectRequest.onerror = function(event) {
            //     reject(Error('Error text'));
            //   };
      
            //   objectRequest.onsuccess = function(event) {
            //     if (objectRequest.result) resolve(objectRequest.result);
            //     else reject(Error('object not found'));
            //   };
            };
          }
    );
}

export function getValueField(field, value){
    if(isObject(value)){
        if(field.type === "multioption"){
            return value.reduce((acc, curr) => acc + " " + curr.multioption, "");
        }
    }
    else{
        return value
    }
    
}

export function postErrorSlack(url, error, info, investigation, appState){
    console.log("Enviamos el error a ", import.meta.env.VITE_APP_SLACK_HOOK2);
    let location = "URL:"+window.location.href;

    let basicText = " URL: "+location+" Error: "+error+ " Message:"+JSON.stringify(info);
    let text = basicText;
    if(investigation){
        text = "Investigation: "+investigation.name+" uuid :"+investigation.uuid+basicText;
    }

    // SHER-499: append the redacted pre-crash app-state snapshot (route, investigation, counts,
    // active form/fields, online flag — no PII) so Slack shows WHAT state the app was in, not just
    // how the user got there. Best-effort; never throws. Callers that don't pass it are unaffected.
    try {
        if (appState && typeof appState === 'object') {
            text += " | appState:"+JSON.stringify(appState).slice(0, 1000);
        }
    } catch (_) { /* never break the reporter */ }

    // Carry the navigation trail + diagnostics sessionId INLINE in the Slack message.
    // The async breadcrumb flush below can be dropped at crash time (401 on the
    // JWT-gated /diagnostics/breadcrumbs, keepalive/rate-limit), in which case the
    // entry path that led to the crash is lost server-side — leaving only the final
    // URL, which is why a search→billing flow reads as "happened in billing".
    // Slack always arrives, so append the trail here as the source of truth for "how
    // did the user get here". Best-effort; never throws.
    try {
        const ctx = getCrashContext();
        text += " | diagSession:"+(ctx.sessionId || "none")
             + " diagEnabled:"+ctx.enabled
             + " trail:["+ctx.trail.join(" > ").slice(0, 1500)+"]";
    } catch (_) { /* never break the reporter */ }

    // Diagnostics: ship the breadcrumb trail (nav + api + state + preceding
    // exceptions) alongside the Slack summary. Slack gets a short message,
    // the diagnostic log gets the full "steps to reproduce" history.
    // Gated at the breadcrumb-library level; no-op when diagnostics are
    // disabled for this investigation.
    try {
        addBreadcrumb('exception', 'slack-error', {
            route: url || window.location?.pathname,
            errorName: error && error.name,
            errorMessage: (error && (error.message || error.stack) || String(error)).slice(0, 200),
        });
        flushBreadcrumbs('slack-error');
    } catch (_) { /* never break the caller path */ }

    // The error reporter must never throw — otherwise it can break the very flow it's
    // reporting on. In dev/test the Slack hook + Mixpanel token are unset, so $.ajax 404s
    // and mixpanel.track() throws ("before_track" on an uninitialised Mixpanel), which was
    // stalling the auth bootstrap (fetchUser never resolved → app stuck on the loader before
    // the login screen). Guard it so a failed report can't take the app down.
    try {
        $.ajax({
            data: 'payload=' + JSON.stringify({
                "text": text
            }),
            dataType: 'json',
            processData: false,
            type: 'POST',
            url: import.meta.env.VITE_APP_SLACK_HOOK2
        });
        mixpanel.track("Error", {
            "error" : error.stack ? error.stack : JSON.stringify(info)
        });
    } catch (_) { /* never break the caller path */ }
}

export function formatData(dataBytes){
    if(dataBytes > 1000000){
      return (dataBytes/1000000).toFixed(2)+"MB";  
    }
    else if(dataBytes > 1000){
          return (dataBytes/1000).toFixed(2)+"KB";
    }
    else{
        return dataBytes+"B";
    }
      
}

export function getCurrentResearcherUuid(){
    const payload = jose.decodeJwt(localStorage.getItem("jwt"));
    return payload.uuid; 
}

export  function datalogger(wrapped){
    return async function() {
        const result = await wrapped.apply(this, arguments);
        const encoder = new TextEncoder();
        const byteArr = encoder.encode(JSON.stringify(result));
        const bytesDownloaded = byteArr.length;
        let total = 0;

        const dateStored = new Date(localStorage.getItem("data_download_date"));
        if(localStorage.getItem("data_download_date") && new Date(localStorage.getItem("data_download_date")).toDateString() === new Date().toDateString()){
            total = parseInt(localStorage.getItem("data_download"));
        }
        else{
            localStorage.setItem("data_download_date", new Date());
        }
        total += bytesDownloaded;
        localStorage.setItem("data_download", total);
        return result;
      }
}

export function sexNumberToString(sex){
    return sex === 0 ? "male" : sex === 1 ? "female" : "any"
}

export function sexStringToColor(sex){
    const sexLowercase = sex.toLowerCase();
    return sexLowercase === "male" ? "#4da7ab" : sexLowercase === "female" ? "#f3948a" : "#aba74d";
}

export const isToday = (someDate) => {
    const today = new Date()
    return someDate.getDate() === today.getDate() &&
      someDate.getMonth() === today.getMonth() &&
      someDate.getFullYear() === today.getFullYear()
  }

export function hasDefaultValues(dict, defaultValues){
for(let key in defaultValues){
    if(dict[key] === defaultValues[key]){
        return true;
    }
}
return false;
}

  export function getDepartmentFromUnit(uuidUnit, departments){
    
    if(departments){
        const department = departments.find((department) =>{
            return department.units.find((unit) => unit.uuid === uuidUnit);
        });
        if(department){
            return department;
        }
    }
    return "";
}

export function getUnitsResearcher(uuidResearcher, researchers){
    const researcher = researchers.find((researcher) => researcher.uuid === uuidResearcher);
    if(researcher){
        return researcher.units;
    }
    return [];
}

export function requestTypeToTranslation(serviceType) {
    const typesService = {
        [ServiceType.LABORATORY]: "laboratory",
        [ServiceType.IMAGING]: "medical-imaging",
        [ServiceType.SHOE]: "shoe_shop", 
        [ServiceType.SURGERY]: "surgery"
    }
    return typesService[serviceType.toString()];
}


export function turnsToSchedule(turn){
    const date = new Date();
    date.setHours(turn[0][0], turn[0][1], 0, 0);
    let stringDate = date.toLocaleTimeString(undefined, {timeStyle:'short'});
    date.setHours(turn[1][0], turn[1][1], 0, 0);
    stringDate += " - "+date.toLocaleTimeString(undefined, {timeStyle:'short'});
    return stringDate;
}

export function countSexPatients(listPatients){
    let tempCountSex = {male : 0, female : 0};
    listPatients.forEach(patient => {
            
        if (patient.personalData.sex.toLowerCase() === "male") {
            tempCountSex.male++;
        }
        else {
            tempCountSex.female++;
        }
        
    })
    return tempCountSex;
}

export function translateOrderOptions(options, orderValue, translate){
    const translatedOptions = options.map((option) => {
        return {value:option.value, label:translate(option.label).toString()}
    }).sort((optionA, optionB) => optionA.label > optionB.label ? 1 : -1);
    //Move the item with value "OTH" to the end of the list
    const otherOption = translatedOptions.find((option) => option.value === orderValue);
    if(otherOption){
        translatedOptions.splice(translatedOptions.indexOf(otherOption), 1);
        translatedOptions.push(otherOption);
    }
    return translatedOptions;
}

/**
 * SHER-1029: the ladder moved to SmartFields/prescriptionDuration, which is the module that owns
 * prescription duration arithmetic and is where the picker needed it too. This is now the
 * two-dates adapter over it, so the prescriptions table, the printed ordonnance and the picker
 * read one implementation instead of two that have to be kept agreeing.
 *
 * The day count is passed through un-rounded on purpose — a fractional span renders as
 * "29.958 days" and must not be promoted to "1 month".
 */
export function differenceDatesToString(dateStart, dateEnd){
    const nDays = (dateEnd.getTime() - dateStart.getTime()) / 86400000;
    const { amount, unit } = daysToAmountAndUnit(nDays);
    return { [unit] : amount };
}

export function twoDigits(number){
    return number < 10 ? "0"+number : number;
}

export function formatCurrency(amount, currency, locale){
    // Map invalid currency codes to valid ISO codes
    const currencyMap = {
        "FCFA": "XOF", // West African CFA franc
        "CFA": "XOF"   // Default CFA to XOF
    };
    
    // Use mapped currency or original currency
    const validCurrency = currencyMap[currency] || currency;
    
    // Remove decimals for CFA currencies
    const cfaCurrencies = ["XOF", "XAF"];
    const options = { style: 'currency', currency: validCurrency };
    if (cfaCurrencies.includes(validCurrency)) {
        options.minimumFractionDigits = 0;
        options.maximumFractionDigits = 0;
    }
    return new Intl.NumberFormat(locale, options).format(amount);
}

/**
 * Treatment Inpatient Edit Window Utilities
 * Treatment inpatient submissions can be edited for 1 year (8760 hours)
 * Regular submissions can be edited for 48 hours
 */

/**
 * Get the edit time window in hours for a submission type
 * @param {boolean} isTreatmentInpatient - Whether this is a treatment_inpatient submission
 * @returns {number} - Edit window in hours
 */
export function getEditTimeWindow(isTreatmentInpatient) {
    return isTreatmentInpatient ? 8760 : 48; // 1 year or 48 hours
}

/**
 * Check if a submission can still be edited based on creation time
 * @param {Date|string|number} createdAt - Creation date/time of the submission
 * @param {boolean} isTreatmentInpatient - Whether this is a treatment_inpatient submission
 * @returns {boolean} - True if still editable
 */
export function canEditSubmission(createdAt, isTreatmentInpatient) {
    if (!createdAt) return false;
    
    const createdDate = new Date(createdAt);
    if (isNaN(createdDate.getTime())) return false;
    
    const now = new Date();
    const hoursElapsed = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60);
    const limit = getEditTimeWindow(isTreatmentInpatient);
    
    return hoursElapsed < limit;
}

// ============================================
// Patient Sync State Management (localStorage-based, per-investigation)
// ============================================

const PATIENT_SYNC_STATE_KEY = 'patient_sync_state';

function getSyncStateMap() {
    try {
        const raw = localStorage.getItem(PATIENT_SYNC_STATE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch (error) {
        console.error('Error reading sync state map:', error);
        return {};
    }
}

function setSyncStateMap(map) {
    try {
        localStorage.setItem(PATIENT_SYNC_STATE_KEY, JSON.stringify(map));
    } catch (error) {
        console.error('Error writing sync state map:', error);
    }
}

/**
 * Get the last synced event ID for event-based incremental sync (per investigation)
 * @param {string} uuidInvestigation - Investigation UUID
 * @returns {number|null} The last event ID, or null if not found
 */
export const getLastSyncEventId = (uuidInvestigation) => {
    if (!uuidInvestigation) return null;
    try {
        const map = getSyncStateMap();
        const entry = map[uuidInvestigation];
        return entry?.lastEventId != null ? parseInt(entry.lastEventId, 10) : null;
    } catch (error) {
        console.error('Error getting last sync event ID:', error);
        return null;
    }
};

/**
 * Set the last synced event ID after a successful sync (per investigation)
 * @param {string} uuidInvestigation - Investigation UUID
 * @param {number} eventId - The event ID to store
 * @param {number} [patientCount] - Optional: number of patients synced (for sanity checks)
 */
export const setLastSyncEventId = (uuidInvestigation, eventId, patientCount) => {
    if (!uuidInvestigation) return;
    try {
        const map = getSyncStateMap();
        map[uuidInvestigation] = {
            lastEventId: eventId,
            lastSyncTimestamp: Date.now(),
            ...(patientCount !== undefined && { lastSyncedPatientCount: patientCount })
        };
        setSyncStateMap(map);
        console.log(`✅ Sync state updated [${uuidInvestigation}]: eventId=${eventId}, patientCount=${patientCount ?? 'N/A'}`);
    } catch (error) {
        console.error('Error setting last sync event ID:', error);
    }
};

/**
 * Get the complete sync state for an investigation
 * @param {string} uuidInvestigation - Investigation UUID
 * @returns {Object|null} Sync state object or null if not found
 * @returns {number} return.lastEventId - Last synced event ID
 * @returns {number} return.lastSyncTimestamp - Timestamp of last sync
 * @returns {number} [return.lastSyncedPatientCount] - Optional patient count
 */
export const getSyncState = (uuidInvestigation) => {
    if (!uuidInvestigation) return null;
    try {
        const map = getSyncStateMap();
        const entry = map[uuidInvestigation];
        if (!entry || entry.lastEventId == null) return null;
        return {
            lastEventId: parseInt(entry.lastEventId, 10),
            lastSyncTimestamp: parseInt(entry.lastSyncTimestamp, 10),
            lastSyncedPatientCount: entry.lastSyncedPatientCount != null ? parseInt(entry.lastSyncedPatientCount, 10) : undefined
        };
    } catch (error) {
        console.error('Error getting sync state:', error);
        return null;
    }
};